import { useEffect, useState } from 'react';

import { useGetResources } from '@hooks/useGetResources';
import { Intensjon } from '@src/types/IntensjonEnum';
import { useAppDispatch, useAppSelector } from '@store';
import { addLaeringsaktivitetThunk, avvisOppgaveLaeringsaktivitetThunk } from '@store/verktoy/thunks';

import type { Verktoy } from '@src/types/minhelseentities';

import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';

import AvvisOppgaveConfirmationModal from './modals/avvis-oppgave-confirmation';
import EnrollOppgaveOptionModal from './modals/enroll-oppgave-option';
import { extractIntensjonFromPathname, extractOppgaveIdFromPathname, extractOppgaveReturUrlFraPathname } from './utils/oppgave-url-utils';

interface VerktoyOppgaveModalContainerProps {
  verktoy: Verktoy;
}

const VerktoyOppgaveModalContainer = ({ verktoy }: VerktoyOppgaveModalContainerProps): React.JSX.Element => {
  const resources = useGetResources();

  const [visEnrollVerktoyOption, setEnrollVerktoyOptionVisning] = useState(false);
  const [visVerktoyCancellation, setVerktoyCancellationVisning] = useState(false);

  const dispatch = useAppDispatch();
  const isOppgaveAvvisSuccess = useAppSelector(globalState => globalState.verktoy.isOppgaveAvvisSuccess);
  const isWaitingForAktivitetResponse = useAppSelector(globalState => globalState.verktoy.isWaitingForAktivitetResponse);
  const isOppgaveEnrollmentSuccess = useAppSelector(globalState => globalState.verktoy.isOppgaveEnrollmentSuccess);

  const oppgaveId = extractOppgaveIdFromPathname();

  const skalViseOppgaveModal = isAuthorized() && !!oppgaveId && !isWaitingForAktivitetResponse && !isOppgaveEnrollmentSuccess;

  useEffect(() => {
    if (skalViseOppgaveModal) {
      setEnrollVerktoyOptionVisning(true);
    } else if (visEnrollVerktoyOption && !oppgaveId) {
      setEnrollVerktoyOptionVisning(false);
    }
  }, [oppgaveId, visEnrollVerktoyOption, skalViseOppgaveModal]);

  useEffect(() => {
    const oppgaveReturUrl = extractOppgaveReturUrlFraPathname();
    if (oppgaveReturUrl && !!isOppgaveAvvisSuccess) {
      window.history.replaceState(window.history.state, '', oppgaveReturUrl);
      window.location.reload();
    }
  }, [isOppgaveAvvisSuccess]);

  useEffect(() => {
    if (isOppgaveEnrollmentSuccess === true) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('oppgaveId');
      window.history.replaceState(window.history.state, '', currentUrl.toString());
    }
  }, [isOppgaveEnrollmentSuccess]);

  const visVerktoyCancellationModal = (): void => {
    const intensjon = extractIntensjonFromPathname();
    if (intensjon === Intensjon.Plan + '') {
      const oppgaveReturUrl = extractOppgaveReturUrlFraPathname();
      window.history.replaceState(window.history.state, '', oppgaveReturUrl);
      window.location.reload();
    } else {
      setVerktoyCancellationVisning(true);
    }

    setEnrollVerktoyOptionVisning(false);
  };

  const confirmVerktoyEnrollment = async (): Promise<void> => {
    if (oppgaveId) {
      await dispatch(addLaeringsaktivitetThunk({ verktoy, oppgaveId })).unwrap();
    }
    setEnrollVerktoyOptionVisning(false);
  };

  const confirmVerktoyCancellation = (): void => {
    setVerktoyCancellationVisning(false);
    if (oppgaveId) {
      dispatch(avvisOppgaveLaeringsaktivitetThunk({ verktoy, oppgaveId }));
    }
  };

  const cancelVerktoyCancellation = (): void => {
    setVerktoyCancellationVisning(false);
    setEnrollVerktoyOptionVisning(true);
  };

  const getIntensjon = (): Intensjon => {
    const intensjonStr = extractIntensjonFromPathname();
    if (!intensjonStr) return Intensjon.Unknown;
    const parsed = parseInt(intensjonStr, 10);
    if (!isNaN(parsed) && parsed in Intensjon) {
      return parsed as Intensjon;
    }
    return Intensjon.Unknown;
  };

  const getModalTitle = (): string => {
    if (resources) {
      const intensjon = extractIntensjonFromPathname();
      if (intensjon === Intensjon.Plan + '') {
        return resources.modal_enroll_tekst_behandler_verktoy;
      } else {
        return resources.modal_enroll_tekst;
      }
    }
    return '';
  };

  const getConfirmBtnLabel = (): string => {
    if (resources) {
      if (getIntensjon() === Intensjon.Plan && resources) {
        return resources.confirmBoxOk;
      } else {
        return resources?.modal_knapp_ja;
      }
    }
    return 'OK';
  };

  const getCancelBtnLabel = (): string => {
    if (resources) {
      if (getIntensjon() === Intensjon.Plan && resources) {
        return resources?.modalCancelButtonText;
      } else {
        return resources?.modal_kanpp_nei;
      }
    }
    return 'Avbryt';
  };

  return (
    <>
      {visEnrollVerktoyOption && (
        <EnrollOppgaveOptionModal
          onConfirmClick={confirmVerktoyEnrollment}
          onCloseClick={visVerktoyCancellationModal}
          modalTitle={getModalTitle()}
          confirmBtnLabel={getConfirmBtnLabel()}
          cancelBtnLabel={getCancelBtnLabel()}
        />
      )}
      {visVerktoyCancellation && (
        <AvvisOppgaveConfirmationModal onConfirm={confirmVerktoyCancellation} onClose={cancelVerktoyCancellation} />
      )}
    </>
  );
};

export default VerktoyOppgaveModalContainer;
